import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import Demarrage from "@/components/Demarrage";
import { money } from "@/lib/roof";
import { useI18n } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Ruler, FileText, TrendingUp, Loader2, Users, ArrowRight } from "lucide-react";

export default function Dashboard() {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const fr = lang !== "en";

  const { data: estimates = [], isLoading } = useQuery({
    queryKey: ["estimates"],
    queryFn: async () => (await api.get("/estimates")).data,
  });
  const { data: leadsData = { leads: [] } } = useQuery({
    queryKey: ["leads"],
    queryFn: async () => (await api.get("/leads")).data,
  });
  const leads = leadsData.leads || [];
  // Sert uniquement à la liste de démarrage : elle lit l'état réel du compte.
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
  });

  const totalValue = estimates.reduce((a, b) => a + (b.total || 0), 0);
  const avg = estimates.length ? totalValue / estimates.length : 0;

  // Statut effectif d'un prospect (les anciens n'ont que `traite`).
  const statutDe = (l) => l.statut || (l.traite ? "accepte_termine" : "en_attente");

  // Répartition des prospects par statut, pour les indicateurs.
  const STATUTS = [
    ["en_attente", fr ? "En attente" : "Pending", "text-ink", "bg-hover-strong"],
    ["en_relance", fr ? "En relance" : "Following up", "text-amber-700", "bg-amber-500/15"],
    ["accepte_a_faire", fr ? "Accepté — à faire" : "Accepted — to do", "text-sky-400", "bg-sky-500/15"],
    ["accepte_termine", fr ? "Accepté — terminé" : "Accepted — done", "text-emerald-400", "bg-emerald-500/15"],
    ["refuse", fr ? "Refusé" : "Refused", "text-red-400", "bg-red-500/15"],
  ];
  const parStatut = useMemo(() => {
    const m = Object.fromEntries(STATUTS.map(([k]) => [k, 0]));
    leads.forEach((l) => {
      const s = statutDe(l);
      if (s in m) m[s] += 1;
    });
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leads]);

  // Cartes du haut. La première mène à la liste complète des estimations.
  const stats = [
    { label: t("dash.statEstimates"), value: estimates.length, icon: FileText, vers: "/estimations" },
    { label: t("dash.statPipeline"), value: money(totalValue), icon: TrendingUp },
    { label: t("dash.statAverage"), value: money(avg), icon: Ruler },
  ];

  return (
    <Layout>
      <div className="p-4 sm:p-6 md:p-10 max-w-6xl">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-ink mb-2">{t("dash.overline")}</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tighter">{t("dash.title")}</h1>
          </div>
          <Button data-testid="dashboard-new-estimate" onClick={() => navigate("/new")} className="bg-orange-600 hover:bg-orange-700 rounded-md font-semibold">
            <Ruler className="h-4 w-4 mr-2" /> {t("dash.newBtn")}
          </Button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand-ink" /></div>
        ) : (
          <>
            <Demarrage settings={settings} nbEstimations={estimates.length} className="mb-6" />

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              {stats.map((s) => {
                const Icon = s.icon;
                const cliquable = !!s.vers;
                return (
                  <div
                    key={s.label}
                    onClick={cliquable ? () => navigate(s.vers) : undefined}
                    data-testid={cliquable ? "stat-estimations" : undefined}
                    className={`bg-surface border border-line rounded-xl p-6 shadow-carte transition-colors ${
                      cliquable ? "cursor-pointer hover:border-line-strong hover:bg-hover group" : ""
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-bold uppercase tracking-[0.2em] text-ink-faint">{s.label}</span>
                      {cliquable
                        ? <ArrowRight className="h-4 w-4 text-ink-faint group-hover:text-brand-ink transition-colors" />
                        : <Icon className="h-4 w-4 text-brand-ink" />}
                    </div>
                    <p className="font-heading text-3xl font-extrabold tracking-tighter">{s.value}</p>
                    {cliquable && (
                      <p className="text-xs text-ink-faint mt-1">{fr ? "Voir vos estimations" : "See your estimates"}</p>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Indicateurs prospects : total + répartition par statut. */}
            <div className="bg-surface border border-line rounded-xl p-6 shadow-carte">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-brand-ink" />
                  <h2 className="font-heading font-bold tracking-tight">
                    {fr ? "Prospects" : "Leads"}{" "}
                    <span className="text-ink-faint font-normal">({leads.length})</span>
                  </h2>
                </div>
                <button
                  onClick={() => navigate("/prospects")}
                  data-testid="voir-prospects"
                  className="text-xs font-semibold text-brand-ink hover:underline inline-flex items-center gap-1"
                >
                  {fr ? "Gérer" : "Manage"} <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {STATUTS.map(([k, lbl, texte, fond]) => (
                  <button
                    key={k}
                    onClick={() => navigate("/prospects")}
                    data-testid={`stat-${k}`}
                    className={`text-left rounded-lg border border-line p-4 hover:border-line-strong transition-colors ${fond}`}
                  >
                    <p className={`font-heading text-2xl font-extrabold tracking-tighter ${texte}`}>{parStatut[k]}</p>
                    <p className="text-[11px] text-ink-soft mt-1 leading-tight">{lbl}</p>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
